export interface ChildMeshMaterialConfig {
  nodeMaterialPath: string;
  environmentIntensity?: number;
}

export interface EnclosureMaterial {
  id: string;
  name: string;
  displayName: string;
  useNodeMaterial?: boolean;
  nodeMaterialPath?: string;
  properties: {
    albedoColor: [number, number, number];
    metallic: number;
    roughness: number;
    environmentIntensity?: number;
    bumpIntensity?: number;
    clearCoatIntensity?: number;
    clearCoatRoughness?: number;
    albedoTexture?: string;
    albedoTextureUV?: { u: number, v: number };
    normalTexture?: string;
    normalTextureUV?: { u: number, v: number };
    metallicRoughnessTexture?: string;
    metallicRoughnessTextureUV?: { u: number, v: number };
    aoTexture?: string;
    aoTextureUV?: { u: number, v: number };
    bumpTexture?: string;
    bumpTextureUV?: { u: number, v: number };
    microSurfaceTexture?: string;
    roughnessTexture?: string;
    roughnessTextureUV?: { u: number, v: number };
    glossinessTexture?: string;
    glossinessTextureUV?: { u: number, v: number };
    specularTexture?: string;
    specularTextureUV?: { u: number, v: number };
    specularLevelTexture?: string;
    specularLevelTextureUV?: { u: number, v: number };
  };
}

export const ENCLOSURE_HARDWARE_MATERIALS = {
  metal: {
    id: 'hardware_metal',
    name: 'hardware_metal',
    displayName: 'Hardware Metal',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Metall.json',
    properties: {
      albedoColor: [.7, .7, .7] as [number, number, number],
      metallic: .9,
      roughness: .1,
      environmentIntensity: 1
    }
  },
  plastic: {
    id: 'hardware_plastic',
    name: 'hardware_plastic',
    displayName: 'Hardware Plastic',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Hardware Plastic.json',
    properties: {
      albedoColor: [0.0001, 0.001, 0.001] as [number, number, number],
      metallic: 0.0,
      roughness: 0.2,
      environmentIntensity: 1.25
    }
  }
} as const;

// Child mesh materials shared across all enclosure color variants.
// Each key matches (partial, case-insensitive) the mesh name in the GLB.
export const ENCLOSURE_CHILD_MESH_MATERIALS: { [meshName: string]: ChildMeshMaterialConfig } = {
  'black text':        { nodeMaterialPath: '/textures/Enclosures/decal background.json', environmentIntensity: 1.0 },
  'decal background':  { nodeMaterialPath: '/textures/Enclosures/decal background.json', environmentIntensity: 1.50 },
  'decal text blue':   { nodeMaterialPath: '/textures/Enclosures/ibanez2.json', environmentIntensity: 3.90 },
  'decal text gray':   { nodeMaterialPath: '/textures/Enclosures/decal letters.json', environmentIntensity: 0.90 },
  'foot metal':        { nodeMaterialPath: '/textures/Enclosures/metal5ts.json', environmentIntensity: 1.90 },
  'metal':             { nodeMaterialPath: '/textures/Enclosures/MMM12.json', environmentIntensity: 0.450 },
  'metal back':        { nodeMaterialPath: '/textures/Enclosures/ibanez2.json', environmentIntensity: 1.5 },
  'plastic gloss':     { nodeMaterialPath: '/textures/Enclosures/matte black.json', environmentIntensity: 0.20 },
  'plastic holder':    { nodeMaterialPath: '/textures/Enclosures/Hardware Plastic.json', environmentIntensity: 1.0 },
  'rubber':            { nodeMaterialPath: '/textures/Enclosures/RUBBERR2.json', environmentIntensity: 1.0 },
  'black plastic bottom':       { nodeMaterialPath: '/textures/Enclosures/matte black.json', environmentIntensity: 0.10 },
  'sticker gray':      { nodeMaterialPath: '/textures/Enclosures/matte black.json', environmentIntensity: 1.0 },
};

export const ENCLOSURE_MATERIALS: EnclosureMaterial[] = [
  {
    id: 'brushed_silver',
    name: 'brushed_silver',
    displayName: 'Black',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Enclosrue Black Texture.json',
    properties: {
      albedoColor: [0.062, 0.322, 0.188],
      metallic: 0.0,
      roughness: 0.5,
      environmentIntensity: 1.2
    }
  },
  {
    id: 'chrome',
    name: 'chrome',
    displayName: 'Gray',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Enclosure Gray Matte.json',
    properties: {
      albedoColor: [0.9, 0.4, 0.6],
      metallic: 0.8,
      roughness: 0.2,
      environmentIntensity: 1.8
    }
  },
  {
    id: 'gloss_black',
    name: 'gloss_black',
    displayName: 'Forest Green',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Enclosure Green Matte.json',
    properties: {
      albedoColor: [0.062, 0.322, 0.188],
      metallic: 0.9,
      roughness: 0.1,
      environmentIntensity: 1
    }
  },
  {
    id: 'blue_matte',
    name: 'blue_matte',
    displayName: 'Blue',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Enclosure Blue Matte.json',
    properties: {
      albedoColor: [0.0, 0.1, 0.8],
      metallic: 0.0,
      roughness: 0.6,
      environmentIntensity: 1.5
    }
  },
  {
    id: 'pink_matte',
    name: 'pink_matte',
    displayName: 'Pink',
    useNodeMaterial: true,
    nodeMaterialPath: '/textures/Enclosures/Enclosure Pink Matte.json',
    properties: {
      albedoColor: [0.9, 0.2, 0.4],
      metallic: 0.0,
      roughness: 0.6,
      environmentIntensity: 1.5
    }
  }
]; 